import { useEffect, useState } from "react";
import { SESSION_TIMEOUT_MS, SESSION_CHECK_INTERVAL_MS } from "../config/constants";

/**
 * Custom hook for session management with inactivity timeout
 * @param {Object|null} user - Current user object
 * @param {Function} onSessionExpire - Callback when session expires
 * @returns {Object} Session state and handlers
 */
export function useSession(user, onSessionExpire) {
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Check for session timeout
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      if (Date.now() - lastActivity > SESSION_TIMEOUT_MS) {
        if (onSessionExpire) {
          onSessionExpire();
        }
      }
    }, SESSION_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [user, lastActivity, onSessionExpire]);

  // Track user activity - listen to all common interaction events
  useEffect(() => {
    const resetActivity = () => setLastActivity(Date.now());

    // Track all common user interactions to prevent false session expiry
    const activityEvents = [
      "mousemove",   // Mouse movement
      "mousedown",   // Mouse clicks
      "keydown",     // Keyboard input
      "scroll",      // Scrolling (includes wheel)
      "touchstart",  // Touch interactions (mobile)
      "click",       // Click events
      "pointerdown", // Pointer events (unified mouse/touch/pen)
    ];

    activityEvents.forEach(event => {
      window.addEventListener(event, resetActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetActivity);
      });
    };
  }, []);

  return {
    lastActivity,
    resetActivity: () => setLastActivity(Date.now()),
  };
}
